import type { Revision, RevisionPage, RevisionPageDocument } from '../../types';
import { mutations } from '../../utils';
import { getPageChildrenInRevision } from './getPageChildrenInRevision';
import { getPageParentInRevision } from './getPageParentInRevision';
import { getRootPagesInRevision } from './getRootPagesInRevision';
import { isRevisionPageDocument, isRevisionPageParent } from './guards';
import {
    insertPageAfterInRevision,
    insertPageBeforeInRevision,
    insertPageInRevision,
} from './insertPageInRevision';

/**
 * Options for restoring a deleted document page into a revision.
 */
export interface RestorePageInRevisionOptions {
    /**
     * Source revision containing the page in its original structural position.
     */
    sourceRevision: Revision;
}

/**
 * Whether a deleted page from the source revision can be restored into the target revision.
 *
 * Deleted document pages are supported, including document subtree roots, as long as the
 * original parent still exists in the target revision.
 */
export function canRestoreDeletedRevisionPage(args: {
    sourceRevision: Revision;
    targetRevision: mutations.MutableData<Revision> | Revision;
    pageId: string;
}): boolean {
    const { sourceRevision, targetRevision, pageId } = args;
    const sourcePage = sourceRevision.pages[pageId];

    if (!sourcePage || !isRevisionPageDocument(sourcePage)) {
        return false;
    }

    const sourceParent = getPageParentInRevision(sourceRevision, pageId);

    return (
        !sourceParent ||
        mutations.getIn(targetRevision, ['pages', sourceParent.id] as const) !== undefined
    );
}

/**
 * Restore a page into a revision using the source revision as the ordering truth.
 * @param revision - The revision to update.
 * @param page - The page snapshot to restore.
 * @param options - Restore options.
 */
export function restorePageInRevision(
    revision: mutations.MutableData<Revision>,
    page: RevisionPageDocument,
    options: RestorePageInRevisionOptions
): void {
    const { sourceRevision } = options;

    if (mutations.getIn(revision, ['pages', page.id] as const)) {
        throw new Error(
            `restorePageInRevision cannot restore page ${page.id} because it already exists in target revision`
        );
    }

    if (
        !canRestoreDeletedRevisionPage({
            sourceRevision,
            targetRevision: revision,
            pageId: page.id,
        })
    ) {
        throw new Error(
            'restorePageInRevision cannot restore a page whose parent was also deleted'
        );
    }

    // Collect descendants before any mutations. Descendants that already exist in the target were
    // moved out before the parent was deleted — skip them and their subtrees entirely so the same
    // page can never end up referenced by two parents.
    const descendantPages = collectDescendantsToRestore(sourceRevision, revision, page);
    const descendantIdsToRestore = new Set(
        descendantPages.map((descendantPage) => descendantPage.id)
    );

    const sourceParent = getPageParentInRevision(sourceRevision, page.id);

    // Keep only descendants that are actually being restored under each restored parent.
    const pageToRestore =
        descendantPages.length > 0
            ? { ...page, pages: page.pages.filter((id) => descendantIdsToRestore.has(id)) }
            : { ...page, pages: [] };

    // Reinsert relative to surviving siblings from the source order so restore collapses the diff
    // back to the original tree shape instead of creating a moved-looking subtree.
    placePageInRevision(revision, sourceRevision, pageToRestore, sourceParent?.id);

    for (const descendantPage of descendantPages) {
        const descendantToRestore = isRevisionPageParent(descendantPage)
            ? {
                  ...descendantPage,
                  pages: descendantPage.pages.filter((id) => descendantIdsToRestore.has(id)),
              }
            : descendantPage;
        mutations.setIn(revision, ['pages', descendantPage.id] as const, descendantToRestore);
    }
}

/**
 * Insert a page into the target revision at the position that best matches its source ordering.
 *
 * Tries in order: before the next surviving sibling, after the previous surviving sibling,
 * then falls back to appending inside the original parent (or at the root if parentless).
 */
function placePageInRevision(
    revision: mutations.MutableData<Revision>,
    sourceRevision: Revision,
    page: RevisionPageDocument,
    parentId: string | undefined
): void {
    const sourceSiblingIds = getPageIdsInRestoreContainer(sourceRevision, parentId);
    const sourcePageIndex = sourceSiblingIds.indexOf(page.id);
    const survivingTargetSiblings = new Set(getPageIdsInRestoreContainer(revision, parentId));

    if (sourcePageIndex < 0) {
        throw new Error(
            `restorePageInRevision expected page ${page.id} to exist in source revision`
        );
    }

    const nextSiblingId = sourceSiblingIds
        .slice(sourcePageIndex + 1)
        .find((id) => survivingTargetSiblings.has(id));

    if (nextSiblingId) {
        insertPageBeforeInRevision(revision, page, nextSiblingId);
        return;
    }

    const previousSiblingId = sourceSiblingIds
        .slice(0, sourcePageIndex)
        .findLast((id) => survivingTargetSiblings.has(id));

    if (previousSiblingId) {
        insertPageAfterInRevision(revision, page, previousSiblingId);
        return;
    }

    insertPageInRevision(revision, page, { into: parentId });
}

function collectDescendantsToRestore(
    sourceRevision: Revision,
    targetRevision: mutations.MutableData<Revision> | Revision,
    page: RevisionPageDocument
): RevisionPage[] {
    const descendants: RevisionPage[] = [];
    // Walk descendants in source order so children are rehydrated with the same structure the
    // restored root already references through its `pages` arrays.
    const pendingDescendantIds = [...page.pages];

    for (let index = 0; index < pendingDescendantIds.length; index += 1) {
        const descendantId = pendingDescendantIds[index];

        if (!descendantId) {
            continue;
        }

        // Descendant already lives in the target — it was moved out before the parent was deleted.
        // Skip it and its subtree entirely so moved pages stay authoritative in the target.
        if (mutations.getIn(targetRevision, ['pages', descendantId] as const) !== undefined) {
            continue;
        }

        const descendantPage = sourceRevision.pages[descendantId];

        if (!descendantPage) {
            throw new Error(
                `restorePageInRevision expected descendant ${descendantId} to exist in source revision`
            );
        }

        descendants.push(descendantPage);

        if (isRevisionPageParent(descendantPage)) {
            pendingDescendantIds.push(...descendantPage.pages);
        }
    }

    return descendants;
}

function getPageIdsInRestoreContainer(
    revision: mutations.MutableData<Revision> | Revision,
    parentId: string | undefined
): string[] {
    // Root-level restores compare against the current root page ordering.
    if (!parentId) {
        return getRootPagesInRevision(revision).map((page) => mutations.get(page, 'id'));
    }

    const parent = mutations.getIn(revision, ['pages', parentId] as const);
    if (!parent || !isRevisionPageParent(parent)) {
        return [];
    }

    // Nested restores compare against the surviving children of the original parent.
    return getPageChildrenInRevision(revision, parentId).map((page) => mutations.get(page, 'id'));
}
